import React, { useCallback, useMemo, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Share,
  Alert,
  Platform,
  ActivityIndicator
} from "react-native";
import TransferSummaryModal from "./TransferSummaryModal";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import { sharePayload } from "../../../../uiComponents/ShareUtils";
import SuccessIcon from "../../../../../../../../assets/images/success-icon-arrow.png";
import CopyIcon from "../../../../../../../../assets/images/copy.png";
import ReceiptIcon from "../../../../../../../../assets/images/receipt.png";
import ShareIcon from "../../../../../../../../assets/images/share.ios.png";

// Example props interface
interface HeadToCashierStepProps {
  status?: string; // e.g. "Staged"
  receiveAmount?: string;
  receiveCurrency?: string;
  onViewSummary?: () => void;
  onShareReceipt?: () => void;
}

const HeadToCashierStep: React.FC<HeadToCashierStepProps> = ({
  receiveAmount,
  receiveCurrency,
  status = "Staged",
  onViewSummary,
  // onShareReceipt,
}) => {
  const kioskId = useSessionStore((s: SessionState) => (s as any).kioskId);
  console.log('Head KIOSKID==>','?t='+kioskId)
  const [summaryVisible, setSummaryVisible] = React.useState(false);
  const [sharing, setSharing] = useState(false);

  // Pull values from store
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const receiverFirstName = useSessionStore((s: SessionState) => (s as any).receiverFirstName);
  const receiverMiddleName = useSessionStore((s: SessionState) => (s as any).receiverMiddleName);
  const receiverLastName = useSessionStore((s: SessionState) => (s as any).receiverLastName);
  const receiverLastName2 = useSessionStore((s: SessionState) => (s as any).receiverLastName2);
  const confirmationNumber = useSessionStore((s: SessionState) => (s as any).confirmationNumber);

  const option: any = pendingSendOption || selectedSendOption || null;

  const storeReceiveCurrency = option ? FeeLookupHelper.getReceiveCurrency(option) : "";
  const receiveAmountNum = option ? FeeLookupHelper.getReceiveAmount(option) : 0;
  const amount = useMemo(() => {
    const num = Number(receiveAmountNum || 0);
    return isFinite(num) ? num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "0.00";
  }, [receiveAmountNum]);
  const receiverName = useMemo(() =>
    [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2].filter(Boolean).join(" "),
    [receiverFirstName, receiverMiddleName, receiverLastName, receiverLastName2]
  );
  const referenceNumber = confirmationNumber || "";

  // Additional computed values for modal
  const sendCurrency = (option && (option.SendAmounts?.SendCurrency || "USD")) || "USD";
  const sendAmountNum = Number(option?.SendAmounts?.SendAmount ?? 0);
  const exchangeRateNum = option ? FeeLookupHelper.getExchangeRate(option) : 0;
  const feesNum = Number(option?.SendAmounts?.TotalDiscountAmount || 0) + Number(option?.SendAmounts?.TotalSendFees || 0);
  const totalNum = Number(option?.TotalAmount || (sendAmountNum + feesNum));

  const buildReceiptText = () => {
    return `MoneyGram Receipt\n\nReceiver: ${receiverName}\nAmount: ${receiveAmount} ${receiveCurrency}\nReference: ${referenceNumber}\nStatus: Staged\n\nThank you for using MoneyGram.`;
  };


  const onShare = useCallback(async () => {
    setSharing(true);
    try {
      const payload = {
        title: "MoneyGram Receipt",
        message: buildReceiptText(),
        // If you have a receipt URL, add it here (helps web share)
        // url: 'https://app.example.com/receipt/0000123456'
      };

      const result = await sharePayload(payload);
      // result has meta info about which path was used
      if (Platform.OS === "web") {
        if ((result as any).usedWebShare) {
          // navigator.share resolved — nothing else to do
          console.log("Shared via navigator.share", result);
        } else if ((result as any).usedClipboard) {
          Alert.alert(
            "Share not available",
            "Share sheet is not supported in this browser. Receipt text has been copied to clipboard — paste it into any app to share."
          );
        } else {
          Alert.alert("Shared", "Sharing completed.");
        }
      } else {
        // Native platforms
        console.log("Native share result:", result);
      }
    } catch (err: any) {
      console.error("Share error:", err);
      Alert.alert("Unable to share", err?.message || "An error occurred while sharing.");
    } finally {
      setSharing(false);
    }
  }, [receiverName, amount, storeReceiveCurrency, referenceNumber]);

  return (
    <View style={styles.container}>
      {/* Icon or Circle Avatar */}
      <View style={styles.circleAvatar}>
        <Image source={SuccessIcon} style={styles.successIconStyle} />
        {/* <Text style={styles.circleText}>{receiverName?.[0] ?? "?"}</Text> */}
      </View>

      {/* Title */}
      <Text style={styles.title}>Head to the cashier!</Text>
      <Text style={styles.subtitle}>Thank you from MoneyGram!</Text>

      {/* Receiver amount */}
      <Text style={styles.receiverLine}>
        {receiverName} will receive
      </Text>
      <Text style={styles.amount}>{amount} {storeReceiveCurrency}</Text>

      {/* Status */}
      <Text style={styles.status}>
        Status: <Text style={styles.statusValue}>{status}</Text>
      </Text>

      {/* Reference Number */}
      <View style={styles.referenceRow}>
        <Text style={styles.referenceLabel}>Reference #</Text>
        <TouchableOpacity style={styles.referenceCopy}>
          <Text style={styles.referenceNumber}>{referenceNumber}</Text>
          {/* You can replace with an icon */}
          <Image source={CopyIcon} style={{ width: 20, height: 20}} />
        </TouchableOpacity>
      </View>

      {/* Instruction */}
      <Text style={styles.instruction}>
        Let the cashier know you need to complete a MoneyGram transaction
      </Text>

      {/* Action Buttons */}
      <View style={styles.actionsRow}>
        <TouchableOpacity
            style={styles.actionBox}
            onPress={() => setSummaryVisible(true)}>
            <View style={styles.receiptView}>
              <Image source={ReceiptIcon} style={{ width: 20, height: 20}} />
            </View>
            <Text>View transfer summary</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.actionBox}
          onPress={onShare}
        >
          <View style={styles.receiptView}>
            <Image source={ShareIcon} style={{width: 20, height: 20}} />
          </View>
          <Text style={styles.actionLabel}>Share receipt</Text>
        </TouchableOpacity>
        {/* Share receipt button */}
      {/* <TouchableOpacity style={styles.shareBtn} onPress={onShareReceipt}>
        <Text style={styles.shareText}>Share receipt</Text>
      </TouchableOpacity> */}
      </View>
      <TransferSummaryModal
        visible={summaryVisible}
        onClose={() => setSummaryVisible(false)}
        receiverName={receiverName || ""}
        amount={`${amount} ${storeReceiveCurrency}`}
        status="Processing"
        paymentMethod={undefined}
        exchangeRate={`1 ${sendCurrency} = ${exchangeRateNum.toFixed(4)} ${storeReceiveCurrency}`}
        fees={`${feesNum.toFixed(2)} ${sendCurrency}`}
        youSent={`${(isFinite(sendAmountNum) ? sendAmountNum : 0).toFixed(2)} ${sendCurrency}`}
        total={`${totalNum.toFixed(2)} ${sendCurrency}`}
        receiveMethod={option?.DeliveryOption || undefined}
      />
        {sharing && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#fff" />
          <Text style={styles.overlayText}>Preparing share …</Text>
        </View>
      )}

      {/* The Continue / I'm Finished button will be rendered by MainSendScreen */}
    </View>
  );
};

export default HeadToCashierStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    paddingHorizontal: 24,
    paddingTop: 20,
  },
  successIconStyle: {
    width: 80,
    height: 80
  },
  circleAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "#eee",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 24,
  },
  circleText: {
    fontSize: 32,
    fontWeight: "600",
    color: "#555",
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: "#666",
    marginBottom: 16,
  },
  receiverLine: {
    fontSize: 16,
    color: "#333",
    marginBottom: 4,
  },
  amount: {
    fontSize: 28,
    fontWeight: "700",
    marginBottom: 12,
  },
  status: {
    fontSize: 14,
    color: "#666",
    marginBottom: 20,
  },
  statusValue: {
    color: "#007AFF", // iOS blue for "Staged"
    fontWeight: "600",
  },
  referenceRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  referenceLabel: {
    fontSize: 14,
    color: "#333",
    marginRight: 8,
  },
  referenceCopy: {
    flexDirection: "row",
    alignItems: "center",
  },
  referenceNumber: {
    color: "#007AFF",
    fontWeight: "600",
    fontSize: 14,
  },
  copyIcon: {
    marginLeft: 4,
    fontSize: 14,
  },
  instruction: {
    fontSize: 14,
    textAlign: "center",
    color: "#444",
    marginBottom: 24,
    paddingHorizontal: 12,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 24,
    width: "100%",
  },
  actionBox: {
    flex: 1,
    alignItems: "center",
    padding: 12,
    marginHorizontal: 6,
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 8,
  },
  receiptView: {
     width: 40,
    height: 40,
    alignItems: "center",
    padding: 12,
    backgroundColor: '#1F1F1F',
    marginBottom: 12,
    // borderWidth: 1,
    // borderColor: "#ddd",
    borderRadius: 30,
  },
  actionIcon: {
    fontSize: 20,
    marginBottom: 6,
  },
  actionLabel: {
    fontSize: 13,
    textAlign: "center",
    color: "#333",
  },
  shareBtn: {
    marginTop: 20,
    backgroundColor: "#000",
    padding: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  shareText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  finishText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "700",
  },
  overlay: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    justifyContent: "center",
    alignItems: "center",
  },
  overlayText: {
    color: "#fff",
    marginTop: 10,
  },
});